// src/pages/LandingPage/DocumentationPage.tsx
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Mail, Key, Users, Briefcase, Search, Building2, Cpu, AlertTriangle, Folder, Network } from 'lucide-react';
import { FindingCard } from '../../components/LandingPage/FindingCard';
import Footer from '../../components/LandingPage/Footer/Footer';

export default function DocumentationPage() {
  const { t } = useTranslation();

  const [selectedInfo, setSelectedInfo] = useState<string | null>(null);
  const infoRef = useRef<HTMLDivElement>(null);

  const findings = [
    {
      icon: Key,
      title: t('documentation.leakedCredentialsTitle'),
      description: t('documentation.leakedCredentialsDesc'),
      moreInfo: t('documentation.leakedCredentialsMoreInfo'),
    },
    {
      icon: Building2,
      title: t('documentation.companyInfoTitle'),
      description: t('documentation.companyInfoDesc'),
      moreInfo: t('documentation.companyInfoMoreInfo'),
    },
    {
      icon: Users,
      title: t('documentation.employeeProfilesTitle'),
      description: t('documentation.employeeProfilesDesc'),
      moreInfo: t('documentation.employeeProfilesMoreInfo'),
    },
    {
      icon: Briefcase,
      title: t('documentation.similarCompaniesTitle'),
      description: t('documentation.similarCompaniesDesc'),
      moreInfo: t('documentation.similarCompaniesMoreInfo'),
    },
    {
      icon: Mail,
      title: t('documentation.emailsTitle'),
      description: t('documentation.emailsDesc'),
      moreInfo: t('documentation.emailsMoreInfo'),
    },
    {
      icon: Globe,
      title: t('documentation.infrastructureSurveillanceTitle'),
      description: t('documentation.infrastructureSurveillanceDesc'),
      moreInfo: t('documentation.infrastructureSurveillanceMoreInfo'),
    },
    {
      icon: AlertTriangle,
      title: t('documentation.vulnerabilitiesTitle'),
      description: t('documentation.vulnerabilitiesDesc'),
      moreInfo: t('documentation.vulnerabilitiesMoreInfo'),
    },
    {
      icon: Search,
      title: t('documentation.typosquattingTitle'),
      description: t('documentation.typosquattingDesc'),
      moreInfo: t('documentation.typosquattingMoreInfo'),
    },
    {
      icon: Folder,
      title: t('documentation.domainExtensionsTitle'),
      description: t('documentation.domainExtensionsDesc'),
      moreInfo: t('documentation.domainExtensionsMoreInfo'),
    },
    {
      icon: Network,
      title: t('documentation.subdomainsTitle'),
      description: t('documentation.subdomainsDesc'),
      moreInfo: t('documentation.subdomainsMoreInfo'),
    },
    {
      icon: Cpu,
      title: t('documentation.technologiesTitle'),
      description: t('documentation.technologiesDesc'),
      moreInfo: t('documentation.technologiesMoreInfo'),
    },
  ];
  

  // Maneja el clic en "Más info"
  function handleMoreInfo(info: string) {
    setSelectedInfo(info);
    // Hacemos scroll al bloque inferior después de actualizar el estado
    setTimeout(() => {
      if (infoRef.current) {
        infoRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  }

  return (
    <div className="flex flex-col bg-gradient-to-b from-black via-[#0A192F] to-[#060D1B] min-h-screen">
      {/* Contenido principal */}
      <div className="container mx-auto px-4 md:px-6 max-w-6xl flex-grow pt-24 pb-0">
        {/* Título e intro */}
        <div className="text-center mb-12 mt-12">
          <h2 className="text-3xl font-bold sm:text-4xl md:text-5xl text-white mb-4">
            {t('documentation.title')}
          </h2>
          <p className="text-gray-300 md:text-xl max-w-2xl mx-auto">
            {t('documentation.subtitle')}
          </p>
        </div>

        {/* Grid con las “tarjetas” */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {findings.map((item) => (
            <FindingCard
              key={item.title}
              icon={item.icon}
              title={item.title}
              description={item.description}
              onMoreInfo={() => handleMoreInfo(item.moreInfo)}
            />
          ))}
        </div>

        {/* Bloque inferior que muestra la info seleccionada */}
        {selectedInfo && (
          <div ref={infoRef} className="mt-10 bg-[#1E293B] p-6 rounded-lg border border-gray-700">
            <h3 className="text-2xl font-bold text-white mb-4">
              Más información
            </h3>
            <p className="text-gray-300 mb-4">
              {selectedInfo}
            </p>
            {/* Botón para cerrar y limpiar la info */}
            <button
              onClick={() => setSelectedInfo(null)}
              className="px-4 py-2 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold"
            >
              Cerrar
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
